import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

export type SearchableMultiSelectOption = {
  label: string;
  value: string | number;
  description?: string;
};

type SearchableMultiSelectProps = {
  label?: string;
  name?: string;
  value: SearchableMultiSelectOption[];
  options: SearchableMultiSelectOption[];
  onChange: (value: SearchableMultiSelectOption[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  multiple?: boolean;
};

const SelectShell = styled.div`
  position: relative;
  width: 100%;
  --search-select-label: #64748b;
  --search-select-text: #0f172a;
  --search-select-muted: #94a3b8;
  --search-select-border: #dbe3ef;
  --search-select-border-strong: #e2e8f0;
  --search-select-focus-border: #93c5fd;
  --search-select-focus-ring: rgba(59, 130, 246, 0.12);
  --search-select-bg: #ffffff;
  --search-select-hover-bg: #fbfdff;
  --search-select-option-hover: #f8fafc;
  --search-select-selected-bg: #eff6ff;
  --search-select-selected-text: #1d4ed8;
  --search-select-primary: #2563eb;
  --search-select-chip-border: #bfdbfe;
  --search-select-footer-border: #f1f5f9;
  --search-select-shadow: 0 16px 34px rgba(15, 23, 42, 0.14);

  body.dark-only & {
    --search-select-label: #a8b0c7;
    --search-select-text: #f8fafc;
    --search-select-muted: #7f89a6;
    --search-select-border: rgba(106, 113, 133, 0.42);
    --search-select-border-strong: rgba(106, 113, 133, 0.5);
    --search-select-focus-border: #5f7cff;
    --search-select-focus-ring: rgba(95, 124, 255, 0.18);
    --search-select-bg: #10101c;
    --search-select-hover-bg: #171827;
    --search-select-option-hover: #171827;
    --search-select-selected-bg: rgba(95, 124, 255, 0.18);
    --search-select-selected-text: #9db0ff;
    --search-select-primary: #7c91ff;
    --search-select-chip-border: rgba(124, 145, 255, 0.38);
    --search-select-footer-border: rgba(106, 113, 133, 0.28);
    --search-select-shadow: 0 16px 34px rgba(2, 6, 23, 0.36);
  }
`;

const SelectLabel = styled.label`
  display: block;
  margin: 0 0 4px 0;
  color: var(--search-select-label);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
`;

const Control = styled.button<{ $isOpen: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 18px;
  align-items: center;
  width: 100%;
  min-height: 34px;
  padding: 5px 9px 5px 10px;
  border: 1px solid
    ${({ $isOpen }) =>
      $isOpen
        ? "var(--search-select-focus-border)"
        : "var(--search-select-border)"};
  border-radius: 7px;
  color: var(--search-select-text);
  background: var(--search-select-bg);
  box-shadow: ${({ $isOpen }) =>
    $isOpen ? "0 0 0 3px var(--search-select-focus-ring)" : "none"};
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--search-select-focus-border);
    background: var(--search-select-hover-bg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const ValueArea = styled.span`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 5px;
`;

const Placeholder = styled.span`
  color: var(--search-select-muted);
  font-size: 12px;
  font-weight: 400;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 140px;
  min-height: 22px;
  padding: 3px 7px;
  border: 1px solid var(--search-select-chip-border);
  border-radius: 999px;
  color: var(--search-select-selected-text);
  background: var(--search-select-selected-bg);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
`;

const ChipText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MoreCount = styled.span`
  color: var(--search-select-label);
  font-size: 11px;
  font-weight: 600;
`;

const Chevron = styled.span<{ $isOpen: boolean }>`
  width: 8px;
  height: 8px;
  justify-self: end;
  border-right: 1.5px solid var(--search-select-label);
  border-bottom: 1.5px solid var(--search-select-label);
  transform: ${({ $isOpen }) =>
    $isOpen ? "rotate(225deg) translate(-1px, -1px)" : "rotate(45deg)"};
  transition: transform 0.15s ease;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--search-select-border-strong);
  border-radius: 8px;
  background: var(--search-select-bg);
  box-shadow: var(--search-select-shadow);
`;

const SearchInput = styled.input`
  width: calc(100% - 16px);
  min-height: 32px;
  margin: 8px;
  padding: 7px 9px;
  border: 1px solid var(--search-select-border);
  border-radius: 7px;
  color: var(--search-select-text);
  background: var(--search-select-bg);
  font-size: 12px;
  outline: none;

  &:focus {
    border-color: var(--search-select-focus-border);
    box-shadow: 0 0 0 3px var(--search-select-focus-ring);
  }

  &::placeholder {
    color: var(--search-select-muted);
  }
`;

const OptionList = styled.div`
  max-height: 230px;
  overflow-y: auto;
  padding: 4px;
`;

const OptionButton = styled.button<{ $active: boolean; $selected: boolean }>`
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  width: 100%;
  padding: 8px 9px;
  border: 0;
  border-radius: 7px;
  color: ${({ $selected }) =>
    $selected
      ? "var(--search-select-selected-text)"
      : "var(--search-select-text)"};
  background: ${({ $active, $selected }) =>
    $selected
      ? "var(--search-select-selected-bg)"
      : $active
      ? "var(--search-select-option-hover)"
      : "var(--search-select-bg)"};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ $selected }) =>
      $selected
        ? "var(--search-select-selected-bg)"
        : "var(--search-select-option-hover)"};
  }
`;

const CheckBox = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? "var(--search-select-primary)"
        : "var(--search-select-border-strong)"};
  border-radius: 5px;
  color: #ffffff;
  background: ${({ $selected }) =>
    $selected ? "var(--search-select-primary)" : "var(--search-select-bg)"};
  font-size: 10px;
  font-weight: 700;
`;

const OptionText = styled.span`
  min-width: 0;
`;

const OptionLabel = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionDescription = styled.span`
  display: block;
  margin-top: 2px;
  color: var(--search-select-label);
  font-size: 11px;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border-top: 1px solid var(--search-select-footer-border);
`;

const FooterButton = styled.button`
  border: 0;
  border-radius: 999px;
  padding: 4px 9px;
  color: var(--search-select-selected-text);
  background: var(--search-select-selected-bg);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--search-select-option-hover);
  }
`;

const FooterSpacer = styled.span`
  flex: 1 1 auto;
`;

const EmptyState = styled.div`
  padding: 12px;
  color: var(--search-select-label);
  font-size: 12px;
  text-align: center;
`;

const SearchableMultiSelect = ({
  label,
  name,
  value,
  options,
  onChange,
  placeholder = "Select options",
  searchPlaceholder = "Search...",
  disabled = false,
  className,
  multiple = true,
}: SearchableMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedValues = useMemo(
    () => new Set(value.map((option) => option.value)),
    [value]
  );

  const filteredOptions = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return options;

    return options.filter((option) => {
      const labelMatch = option.label.toLowerCase().includes(search);
      const valueMatch = String(option.value).toLowerCase().includes(search);
      const descriptionMatch = option.description
        ?.toLowerCase()
        .includes(search);

      return labelMatch || valueMatch || descriptionMatch;
    });
  }, [options, query]);

  const allFilteredSelected = useMemo(
    () =>
      filteredOptions.length > 0 &&
      filteredOptions.every((option) => selectedValues.has(option.value)),
    [filteredOptions, selectedValues]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(0);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen]);

  const toggleOption = (option: SearchableMultiSelectOption) => {
    if (!multiple) {
      onChange([option]);
      setQuery("");
      setIsOpen(false);
      return;
    }

    const exists = selectedValues.has(option.value);
    const nextValue = exists
      ? value.filter((selected) => selected.value !== option.value)
      : [...value, option];

    onChange(nextValue);
  };

  const handleToggleAll = () => {
    if (!multiple || !filteredOptions.length) return;

    const filteredValueSet = new Set(
      filteredOptions.map((option) => option.value)
    );

    if (allFilteredSelected) {
      onChange(
        value.filter((selected) => !filteredValueSet.has(selected.value))
      );
      return;
    }

    const nextValue = [...value];
    filteredOptions.forEach((option) => {
      if (!selectedValues.has(option.value)) {
        nextValue.push(option);
      }
    });

    onChange(nextValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen && ["ArrowDown", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) =>
        Math.min(current + 1, filteredOptions.length - 1)
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter" && filteredOptions[activeIndex]) {
      event.preventDefault();
      toggleOption(filteredOptions[activeIndex]);
    }
  };

  const visibleChips = value.slice(0, 1);
  const hiddenCount = Math.max(value.length - visibleChips.length, 0);
  const canShowMultiActions = multiple && (options.length > 0 || value.length > 0);
  const canShowSelectAll = multiple && filteredOptions.length > 0;

  return (
    <SelectShell
      ref={rootRef}
      className={className}
      onKeyDown={handleKeyDown}
    >
      {label && <SelectLabel htmlFor={name}>{label}</SelectLabel>}
      <Control
        id={name}
        type="button"
        $isOpen={isOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <ValueArea>
          {value.length > 0 ? (
            <>
              {visibleChips.map((option) => (
                <Chip key={option.value}>
                  <ChipText>{option.label}</ChipText>
                </Chip>
              ))}
              {hiddenCount > 0 && <MoreCount>+{hiddenCount}</MoreCount>}
            </>
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )}
        </ValueArea>
        <Chevron $isOpen={isOpen} />
      </Control>

      {isOpen && (
        <Menu>
          <SearchInput
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
          />
          <OptionList role="listbox" aria-multiselectable={multiple}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const selected = selectedValues.has(option.value);

                return (
                  <OptionButton
                    key={option.value}
                    type="button"
                    $active={index === activeIndex}
                    $selected={selected}
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => toggleOption(option)}
                  >
                    <CheckBox $selected={selected} />
                    <OptionText>
                      <OptionLabel>{option.label}</OptionLabel>
                      {option.description && (
                        <OptionDescription>
                          {option.description}
                        </OptionDescription>
                      )}
                    </OptionText>
                  </OptionButton>
                );
              })
            ) : (
              <EmptyState>No options found</EmptyState>
            )}
          </OptionList>
          {canShowMultiActions && (
            <MenuFooter>
              {canShowSelectAll && (
                <FooterButton type="button" onClick={handleToggleAll}>
                  {allFilteredSelected
                    ? query.trim()
                      ? "Clear results"
                      : "Clear all"
                    : query.trim()
                    ? "Select results"
                    : "Select all"}
                </FooterButton>
              )}
              <FooterSpacer />
              {value.length > 0 && (
                <FooterButton type="button" onClick={() => onChange([])}>
                  Clear
                </FooterButton>
              )}
              <FooterButton type="button" onClick={() => setIsOpen(false)}>
                Done
              </FooterButton>
            </MenuFooter>
          )}
        </Menu>
      )}
    </SelectShell>
  );
};

export default SearchableMultiSelect;
